import React, { useRef, useEffect, useState } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Space,
  Typography,
  Tag,
  Alert,
  Switch,
  Slider,
  Select,
  Tooltip,
  Badge,
  Segmented,
} from 'antd';
import {
  ExpandOutlined,
  CompressOutlined,
  ReloadOutlined,
  SettingOutlined,
  EyeOutlined,
  FullscreenOutlined,
  ScanOutlined,
  HeatMapOutlined,
  BoxPlotOutlined,
  HomeOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from "react-i18next";

const { Title, Text } = Typography;
const { Option } = Select;

// 3D視圖相關接口
interface Warehouse3DViewProps {
  warehouse: {
    id: string;
    name: string;
    code: string;
  };
  locations: Array<{
    id: string;
    code: string;
    name: string;
    type: string;
    coordinates: { x: number; y: number; z?: number };
    utilizationRate?: number;
    isActive: boolean;
  }>;
  onLocationSelect?: (locationId: string) => void;
}

interface ViewSettings {
  showUtilization: boolean;
  showLabels: boolean;
  showGridLines: boolean;
  viewAngle: 'top' | 'side' | 'isometric' | '3d';
  colorMode: 'utilization' | 'type' | 'status';
  zoom: number;
}

const Warehouse3DView: React.FC<Warehouse3DViewProps> = ({
  warehouse,
  locations,
  onLocationSelect,
}) => {
    const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [viewSettings, setViewSettings] = useState<ViewSettings>({
    showUtilization: true,
    showLabels: true,
    showGridLines: true,
    viewAngle: 'isometric',
    colorMode: 'utilization',
    zoom: 1,
  });

  // 位置類型顏色配置
  const locationColors = {
    ZONE: '#1890ff',
    AISLE: '#52c41a',
    RACK: '#faad14',
    SHELF: '#722ed1',
    BIN: '#f5222d',
    PACKING_ZONE: '#eb2f96',
  };

  // 使用率顏色映射
  const getUtilizationColor = (rate: number): string => {
    if (rate >= 90) return '#ff4d4f';
    if (rate >= 70) return '#faad14';
    if (rate >= 50) return '#52c41a';
    return '#1890ff';
  };

  // 繪製3D視圖
  const draw3DView = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

     // 清除畫佈
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // 設置畫佈大小
    const { width, height } = canvas.getBoundingClientRect();
    canvas.width = width;
    canvas.height = height;

    // 繪製網格線（如果啟用）
    if (viewSettings.showGridLines) {
      drawGrid(ctx, width, height);
    }

    // 繪製位置
    locations.forEach(location => {
      drawLocation(ctx, location, width, height);
    });

    // 繪製圖例
    drawLegend(ctx, width, height);
  };

  // 繪製網格線
  const drawGrid = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    ctx.strokeStyle = '#e0e0e0';
    ctx.lineWidth = 1;
    
    const gridSize = 20 * viewSettings.zoom;
    
    // 垂直線
    for (let x = 0; x <= width; x += gridSize) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
    
    // 水平線
    for (let y = 0; y <= height; y += gridSize) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
    }
  };

  // 繪製位置
  const drawLocation = (
    ctx: CanvasRenderingContext2D, 
    location: any, 
    width: number, 
    height: number
  ) => {
    const scale = viewSettings.zoom;
    const offsetX = width / 2;
    const offsetY = height / 2;
    
    // 計算位置座標
    let x = location.coordinates.x * scale + offsetX;
    let y = location.coordinates.y * scale + offsetY;
    
    // 根據視角調整座標
    if (viewSettings.viewAngle === 'isometric') {
      x = (location.coordinates.x - location.coordinates.y) * scale * 0.5 + offsetX;
      y = (location.coordinates.x + location.coordinates.y) * scale * 0.25 + offsetY;
    }

    // 確定顏色
    let color = locationColors.ZONE;
    if (viewSettings.colorMode === 'utilization' && location.utilizationRate !== undefined) {
      color = getUtilizationColor(location.utilizationRate);
    } else if (viewSettings.colorMode === 'type') {
      color = locationColors[location.type as keyof typeof locationColors] || '#666';
    } else if (viewSettings.colorMode === 'status') {
      color = location.isActive ? '#52c41a' : '#ff4d4f';
    }

    // 繪製位置形狀
    ctx.fillStyle = color;
    ctx.strokeStyle = selectedLocation === location.id ? '#000' : '#333';
    ctx.lineWidth = selectedLocation === location.id ? 3 : 1;

    const size = getLocationSize(location.type) * scale;
    
    if (location.type === 'ZONE') {
      // 繪製區域（矩形）
      ctx.fillRect(x - size/2, y - size/2, size, size * 0.6);
      ctx.strokeRect(x - size/2, y - size/2, size, size * 0.6);
    } else if (location.type === 'RACK') {
      // 繪製貨架（帶3D效果的矩形）
      ctx.fillRect(x - size/2, y - size/2, size, size);
      ctx.strokeRect(x - size/2, y - size/2, size, size);
      
      // 3D效果
      ctx.fillStyle = shadeColor(color, -20);
      ctx.fillRect(x - size/2 + 2, y - size/2 + 2, size, size);
    } else {
      // 繪製其他類型（圓形）
      ctx.beginPath();
      ctx.arc(x, y, size/2, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();
    }

    // 繪製標籤（如果啟用）
    if (viewSettings.showLabels) {
      ctx.fillStyle = '#000';
      ctx.font = `${10 * scale}px Arial`;
      ctx.textAlign = 'center';
      ctx.fillText(location.code, x, y + size/2 + 15);
    }

    // 繪製使用率指示器（如果啟用）
    if (viewSettings.showUtilization && location.utilizationRate !== undefined) {
      const barWidth = size;
      const barHeight = 4;
      const barY = y + size/2 + 5;
      
      // 背景條
      ctx.fillStyle = '#e0e0e0';
      ctx.fillRect(x - barWidth/2, barY, barWidth, barHeight);
      
      // 使用率條
      ctx.fillStyle = getUtilizationColor(location.utilizationRate);
      ctx.fillRect(x - barWidth/2, barY, barWidth * (location.utilizationRate / 100), barHeight);
    }
  };

  // 獲取位置大小
  const getLocationSize = (type: string): number => {
    const sizes = {
      ZONE: 40,
      AISLE: 30,
      RACK: 20,
      SHELF: 15,
      BIN: 10,
      PACKING_ZONE: 35,
    };
    return sizes[type as keyof typeof sizes] || 20;
  };

  // 顏色深淺處理
  const shadeColor = (color: string, percent: number): string => {
    const num = parseInt(color.replace("#",""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  };

  // 繪製圖例
  const drawLegend = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    const legendX = width - 150;
    const legendY = 20;
    
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(legendX - 10, legendY - 10, 140, 120);
    ctx.strokeStyle = '#ccc';
    ctx.strokeRect(legendX - 10, legendY - 10, 140, 120);
    
    ctx.fillStyle = '#000';
    ctx.font = '12px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(t('圖例'), legendX, legendY + 5);
    
    // 根據顏色模式顯示不同的圖例
    if (viewSettings.colorMode === 'utilization') {
      const utilizationLevels = [
        { label: t('高使用率 (90%+)'), color: '#ff4d4f' },
        { label: t('中使用率 (70-90%)'), color: '#faad14' },
        { label: t('低使用率 (50-70%)'), color: '#52c41a' },
        { label: t('很低 (<50%)'), color: '#1890ff' },
      ];
      
      utilizationLevels.forEach((level, index) => {
        const y = legendY + 20 + index * 20;
        ctx.fillStyle = level.color;
        ctx.fillRect(legendX, y, 12, 12);
        ctx.fillStyle = '#000';
        ctx.font = '10px Arial';
        ctx.fillText(level.label, legendX + 16, y + 9);
      });
    } else if (viewSettings.colorMode === 'type') {
      Object.entries(locationColors).forEach(([type, color], index) => {
        const y = legendY + 20 + index * 15;
        ctx.fillStyle = color;
        ctx.fillRect(legendX, y, 12, 12);
        ctx.fillStyle = '#000';
        ctx.font = '10px Arial';
        ctx.fillText(type, legendX + 16, y + 9);
      });
    }
  };

  // 處理畫佈點擊
  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    // 查找點擊的位置
    const clickedLocation = findLocationAtPoint(x, y);
    if (clickedLocation) {
      setSelectedLocation(clickedLocation.id);
      if (onLocationSelect) {
        onLocationSelect(clickedLocation.id);
      }
    }
  };

  // 查找指定點的位置
  const findLocationAtPoint = (x: number, y: number) => {
    const scale = viewSettings.zoom;
    const offsetX = canvasRef.current!.width / 2;
    const offsetY = canvasRef.current!.height / 2;

    return locations.find(location => {
      let locX = location.coordinates.x * scale + offsetX;
      let locY = location.coordinates.y * scale + offsetY;
      
      if (viewSettings.viewAngle === 'isometric') {
        locX = (location.coordinates.x - location.coordinates.y) * scale * 0.5 + offsetX;
        locY = (location.coordinates.x + location.coordinates.y) * scale * 0.25 + offsetY;
      }

      const size = getLocationSize(location.type) * scale;
      const distance = Math.sqrt((x - locX) ** 2 + (y - locY) ** 2);
      
      return distance <= size / 2;
    });
  };

  // 重繪視圖
  useEffect(() => {
    draw3DView();
  }, [locations, viewSettings, selectedLocation]);

  // 切換全屏
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div style={{ position: isFullscreen ? 'fixed' : 'relative', 
                  top: isFullscreen ? 0 : 'auto',
                  left: isFullscreen ? 0 : 'auto',
                  width: isFullscreen ? '100vw' : '100%',
                  height: isFullscreen ? '100vh' : '600px',
                  zIndex: isFullscreen ? 1000 : 'auto',
                  backgroundColor: isFullscreen ? '#fff' : 'transparent' }}>
      
      {/* 控制面板 */}
      <Card 
        title={t('3D倉庫視圖 - {{name}}', { name: warehouse.name })}
        extra={
          <Space>
            <Button
              icon={isFullscreen ? <CompressOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={draw3DView}
            />
          </Space>
        }
        style={{ marginBottom: isFullscreen ? 0 : 16 }}
      >
        {/* 設置面板 */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={4}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>{t('視角:')}</Text>
              <Segmented
                value={viewSettings.viewAngle}
                onChange={(value) => setViewSettings(prev => ({ ...prev, viewAngle: value as any }))}
                options={[
                  { label: t('頂視'), value: 'top' },
                  { label: t('側視'), value: 'side' },
                  { label: t('等角'), value: 'isometric' },
                  { label: '3D', value: '3d' },
                ]}
                size="small"
              />
            </Space>
          </Col>
          
          <Col span={4}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>{t('顏色模式:')}</Text>
              <Select
                value={viewSettings.colorMode}
                onChange={(value) => setViewSettings(prev => ({ ...prev, colorMode: value }))}
                style={{ width: '100%' }}
                size="small"
              >
                <Option value="utilization">{t('使用率')}</Option>
                <Option value="type">{t('類型')}</Option>
                <Option value="status">{t('狀態')}</Option>
              </Select>
            </Space>
          </Col>
          
          <Col span={4}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>{t('縮放:')}</Text>
              <Slider
                min={0.5}
                max={3}
                step={0.1}
                value={viewSettings.zoom}
                onChange={(value) => setViewSettings(prev => ({ ...prev, zoom: value }))}
              />
            </Space>
          </Col>
          
          <Col span={8}>
            <Space direction="vertical" size="small">
              <Text strong>{t('顯示選項:')}</Text>
              <Space>
                <Switch
                  checked={viewSettings.showLabels}
                  onChange={(checked) => setViewSettings(prev => ({ ...prev, showLabels: checked }))}
                  size="small"
                />
                <Text>{t('標籤')}</Text>
              </Space>
              <Space>
                <Switch
                  checked={viewSettings.showUtilization}
                  onChange={(checked) => setViewSettings(prev => ({ ...prev, showUtilization: checked }))}
                  size="small"
                />
                <Text>{t('使用率')}</Text>
              </Space>
              <Space>
                <Switch
                  checked={viewSettings.showGridLines}
                  onChange={(checked) => setViewSettings(prev => ({ ...prev, showGridLines: checked }))}
                  size="small"
                />
                <Text>{t('網格')}</Text>
              </Space>
            </Space>
          </Col>
          
          <Col span={4}>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>{t('統計:')}</Text>
              <div>
                <Badge count={locations.length} />
                <Text style={{ marginLeft: 8 }}>{t('總位置')}</Text>
              </div>
              <div>
                <Badge count={locations.filter(l => l.isActive).length} status="success" />
                <Text style={{ marginLeft: 8 }}>{t('活躍')}</Text>
              </div>
            </Space>
          </Col>
        </Row>

        {/* 3D視圖畫佈 */}
        <div style={{ 
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          overflow: 'hidden',
          height: isFullscreen ? 'calc(100vh - 200px)' : '500px',
          position: 'relative'
        }}>
          <canvas
            ref={canvasRef}
            onClick={handleCanvasClick}
            style={{
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              display: 'block'
            }}
          />
          
          {/* 選中位置信息 */}
          {selectedLocation && (
            <div style={{
              position: 'absolute',
              top: 10,
              left: 10,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #d9d9d9',
              fontSize: '12px'
            }}>
              <Text strong>{t('選中位置:')}</Text>
              <br />
              {(() => {
                const location = locations.find(l => l.id === selectedLocation);
                return location ? (
                  <div>
                    <Text code>{location.code}</Text>
                    <br />
                    <Text>{location.name}</Text>
                    {location.utilizationRate && (
                      <>
                        <br />
                        <Text>{t('使用率:')} {location.utilizationRate}%</Text>
                      </>
                    )}
                  </div>
                ) : null;
              })()}
            </div>
          )}
        </div>

        {/* 操作提示 */}
        <Alert
          message={t('操作提示')}
          description={t('點擊位置可查看詳細信息；使用控制面板調整視圖設置；支持多種視角和顏色模式')}
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      </Card>
    </div>
  );
};

export default Warehouse3DView;